"use client";

import Image from "next/image";
import Link from "next/link";
import Script from "next/script";
import { getRecaptchaToken } from "@/lib/recaptcha-browser";
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Check,
  ClipboardList,
  Clock3,
  Home,
  MessageCircle,
  PieChart,
  Sparkles,
  Users,
  Wallet,
} from "lucide-react";
import { FormEvent, useMemo, useState } from "react";
import { getMetaClickIds, persistMetaLead, trackPixelEvent } from "@/lib/meta/browser";
import styles from "./page.module.css";

const FLEXPRO_WHATSAPP = "551142805787";

const roles = [
  {
    label: "Gestor(a) / Sócio(a)",
    detail: "visão da operação",
    icon: BarChart3,
  },
  {
    label: "Corretor(a)",
    detail: "leads e vendas",
    icon: Home,
  },
  {
    label: "Auxiliar administrativo",
    detail: "rotina e processos",
    icon: ClipboardList,
  },
  {
    label: "Financeiro / Locação",
    detail: "controle e cobranças",
    icon: Wallet,
  },
  {
    label: "Marketing / Atendimento",
    detail: "captação e relacionamento",
    icon: MessageCircle,
  },
  {
    label: "Outro setor",
    detail: "continuar",
    icon: Sparkles,
  },
] as const;

const questions = [
  {
    title: "Hoje, onde estão as principais informações da sua imobiliária?",
    options: [
      ["Em um sistema centralizado, com boa parte da operação integrada.", 3],
      ["Temos sistema, mas ainda usamos muitas planilhas e controles paralelos.", 2],
      ["As informações ficam espalhadas entre planilhas, WhatsApp e anotações.", 1],
      ["Depende muito de cada pessoa lembrar ou procurar manualmente.", 0],
    ],
  },
  {
    title: "Quando um lead chega, como acontece o acompanhamento?",
    options: [
      ["Existe processo claro, registro, responsáveis e acompanhamento por etapa.", 3],
      ["Acompanhamos, mas nem sempre fica claro o histórico ou o próximo passo.", 2],
      ["Depende do corretor lembrar e responder no tempo certo.", 1],
      ["Muitos leads se perdem ou ficam sem retorno estruturado.", 0],
    ],
  },
  {
    title: "Como está o controle financeiro, boletos, repasses e cobranças?",
    options: [
      ["Bem organizado, com processos definidos e conferência frequente.", 3],
      ["Funciona, mas ainda exige bastante conferência manual.", 2],
      ["Usamos muitos controles separados para evitar erros.", 1],
      ["É uma das áreas que mais gera retrabalho, dúvida ou atraso.", 0],
    ],
  },
  {
    title: "Se alguém da equipe sai de férias ou falta, a operação continua bem?",
    options: [
      ["Sim, porque os processos e informações ficam registrados.", 3],
      ["Continua, mas algumas coisas ficam mais lentas.", 2],
      ["Depende muito de pedir informação para outra pessoa.", 1],
      ["A operação fica travada em várias situações.", 0],
    ],
  },
  {
    title: "Você consegue acompanhar indicadores reais da imobiliária?",
    options: [
      ["Sim, usamos dados para acompanhar leads, contratos, financeiro e produtividade.", 3],
      ["Temos alguns indicadores, mas ainda falta visão geral.", 2],
      ["Só conseguimos analisar quando alguém monta relatórios manualmente.", 1],
      ["Hoje tomamos decisões mais pela percepção do que por dados.", 0],
    ],
  },
  {
    title: "O quanto a rotina ainda depende de retrabalho?",
    options: [
      ["Pouco. A operação é bem automatizada e integrada.", 3],
      ["Moderado. Existem processos bons, mas alguns pontos ainda são manuais.", 2],
      ["Bastante. Muitas informações precisam ser copiadas ou conferidas mais de uma vez.", 1],
      ["Muito. A equipe perde tempo com tarefas repetitivas todos os dias.", 0],
    ],
  },
] as const;

const optionHelp = [
  "Cenário mais estruturado",
  "Cenário intermediário",
  "Cenário com pontos manuais",
  "Cenário que merece atenção",
] as const;

const testimonials = [
  {
    quote:
      "Chegou um ponto em que a planilha não sustentava mais. Com o SIMOB, centralizamos tudo, eliminamos o retrabalho e hoje tomamos decisões baseadas em dados reais.",
    name: "Imobiliária Pedretti",
    role: "José Carlos & Ricardo Pedretti",
  },
  {
    quote:
      "Temos processos bem definidos a partir da ferramenta da Flexpro. É uma plataforma essencial para nós. Não conseguimos mais trabalhar sem, porque temos eficiência.",
    name: "D e G Imóveis",
    role: "Simone",
  },
  {
    quote:
      "Funções que antes levavam 3 dias para serem feitas, como gerar boletos, hoje são efetuadas em minutos. Isso dá disponibilidade de usarmos o tempo em outras áreas.",
    name: "Pietro Imobiliária",
    role: "Mari",
  },
] as const;

type Answer = {
  index: number;
  label: string;
  points: number;
};

type LeadData = {
  nome?: string;
  imobiliaria?: string;
  email?: string;
  whatsapp?: string;
  cidade?: string;
  imoveis?: string;
  desafio?: string;
  comentario?: string;
};

type Stage = "role" | "quiz" | "lead" | "loading" | "result" | "about" | "whatsapp";

function getDiagnosis(score: number) {
  if (score <= 6) {
    return {
      name: "Operação em alerta",
      text: "Sua imobiliária parece conviver com um nível alto de caos operacional: processos manuais, controles paralelos e conhecimento concentrado em pessoas.",
      tip: "O ponto mais importante agora é entender quanto esses processos internos mal definidos estão custando em tempo, controle e produtividade.",
    };
  }

  if (score <= 12) {
    return {
      name: "Gestão em estruturação",
      text: "Sua operação já tem alguma organização, mas ainda existem gargalos que podem limitar produtividade, controle e previsibilidade.",
      tip: "Aqui faz sentido revisar onde a equipe ainda perde tempo e quais áreas precisam conversar melhor entre si.",
    };
  }

  if (score <= 16) {
    return {
      name: "Operação organizada",
      text: "Sua imobiliária demonstra um bom nível de controle, mas ainda pode ganhar eficiência com integração, automação e indicadores mais claros.",
      tip: "O próximo passo é reduzir retrabalho e melhorar a visibilidade do que acontece em cada etapa da operação.",
    };
  }

  return {
    name: "Gestão madura",
    text: "Sua imobiliária já parece ter uma operação bem estruturada. Nesse estágio, a tecnologia pode ajudar a escalar, padronizar processos e aprofundar a gestão por dados.",
    tip: "Vale conversar sobre evolução, produtividade e oportunidades de ganho operacional.",
  };
}

export default function DiagnosticoRapido() {
  const [stage, setStage] = useState<Stage>("role");
  const [role, setRole] = useState("");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<(Answer | undefined)[]>([]);
  const [leadData, setLeadData] = useState<LeadData>({});
  const [honeypot, setHoneypot] = useState("");
  const [submitStatus, setSubmitStatus] = useState<"idle" | "loading" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const score = useMemo(
    () => answers.reduce((total, answer) => total + (answer?.points ?? 0), 0),
    [answers],
  );
  const diagnosis = useMemo(() => getDiagnosis(score), [score]);
  const progress = Math.round(((stage === "quiz" ? step : questions.length) / questions.length) * 100);
  const currentQuestion = questions[step] ?? questions[0];
  const selectedAnswer = answers[step];

  function selectRole(value: string) {
    setRole(value);
    window.localStorage.setItem("flexpro_role", value);
    setStage("quiz");
  }

  function selectOption(index: number) {
    const option = currentQuestion.options[index];
    if (!option) return;

    const nextAnswers = [...answers];
    nextAnswers[step] = {
      index,
      label: option[0],
      points: option[1],
    };
    setAnswers(nextAnswers);
  }

  function nextQuestion() {
    if (!selectedAnswer) return;
    if (step < questions.length - 1) {
      setStep((current) => current + 1);
      return;
    }
    setStage("lead");
  }

  function goBack() {
    if (stage === "quiz" && step === 0) {
      setStage("role");
      return;
    }
    if (stage === "quiz") {
      setStep((current) => Math.max(0, current - 1));
      return;
    }
    if (stage === "lead") {
      setStage("quiz");
      setStep(questions.length - 1);
      return;
    }
    if (stage === "about") {
      setStage("result");
    }
  }

  function submitLead(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void sendLeadToCrm(event.currentTarget);
  }

  function formatPhone(value: string) {
    const numbers = value.replace(/\D/g, "").slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  }

  function formatEmail(value: string) {
    return value.replace(/\s/g, "").toLowerCase();
  }

  function getUtmParams() {
    const queryParams = new URLSearchParams(window.location.search);
    return {
      utmSource: queryParams.get("utm_source") || "",
      utmMedium: queryParams.get("utm_medium") || "",
      utmCampaign: queryParams.get("utm_campaign") || "",
      utmContent: queryParams.get("utm_content") || "",
      utmTerm: queryParams.get("utm_term") || "",
    };
  }

  function getAnswersSummary() {
    return answers
      .map((answer, index) => `${index + 1}. ${questions[index]?.title ?? "Pergunta"}\nResposta: ${answer?.label ?? "Não respondido"}`)
      .join("\n\n");
  }

  function buildCrmObservation(data: LeadData) {
    return [
      "Diagnóstico rápido para imobiliárias",
      `Cargo/setor informado: ${role || "Não informado"}`,
      `Imobiliária: ${data.imobiliaria || "Não informado"}`,
      `Cidade/Estado: ${data.cidade || "Não informado"}`,
      `Quantidade aproximada de imóveis: ${data.imoveis || "Não informado"}`,
      `Principal desafio hoje: ${data.desafio || "Não informado"}`,
      `Resultado calculado: ${diagnosis.name}`,
      `Pontuação: ${score} de ${questions.length * 3}`,
      "",
      "Respostas do diagnóstico:",
      getAnswersSummary(),
      "",
      `Comentário adicional: ${data.comentario || "Não informado"}`,
    ].join("\n");
  }


  async function sendLeadToCrm(form: HTMLFormElement) {
    setSubmitStatus("loading");
    setErrorMessage("");

    const data = Object.fromEntries(new FormData(form).entries()) as LeadData;
    setLeadData(data);

    if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      setSubmitStatus("error");
      setErrorMessage("Por favor, informe um e-mail válido.");
      return;
    }

    if (!data.whatsapp || data.whatsapp.replace(/\D/g, "").length < 10) {
      setSubmitStatus("error");
      setErrorMessage("Por favor, informe um WhatsApp válido com DDD.");
      return;
    }

    try {
      const recaptchaToken = await getRecaptchaToken();
      const meta = getMetaClickIds();
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          nome: data.nome,
          email: data.email,
          telefone: data.whatsapp,
          assunto: "Diagnóstico rápido para imobiliárias",
          contratos: data.imoveis,
          mensagem: buildCrmObservation(data),
          categoria: 1351,
          origem: "Site",
          etiqueta: "Diagnóstico rápido",
          ...getUtmParams(),
          _honeypot: honeypot,
          recaptchaToken,
          eventId: meta.eventId,
          fbp: meta.fbp,
          fbc: meta.fbc,
          eventSourceUrl: meta.eventSourceUrl,
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Não foi possível enviar o diagnóstico para o CRM.");
      }

      setSubmitStatus("idle");
      persistMetaLead({ email: data.email, phone: data.whatsapp, name: data.nome });
      trackPixelEvent("Lead", result.eventId || meta.eventId, {
        content_name: "Diagnóstico rápido",
        content_category: "lead",
        status: "submitted",
      });
      setStage("loading");
      window.setTimeout(() => setStage("result"), 900);
    } catch (error) {
      setSubmitStatus("error");
      setErrorMessage(error instanceof Error ? error.message : "Erro ao enviar o diagnóstico.");
    }
  }

  function buildWhatsappUrl() {
    const resumo = answers
      .map((answer, index) => `${index + 1}. ${questions[index]?.title ?? "Pergunta"} - ${answer?.label ?? "Não respondido"}`)
      .join("\n");

    const msg = `Olá, respondi o diagnóstico de gestão imobiliária da Flexpro e gostaria de conversar com um especialista.

Nome: ${leadData.nome || "Não informado"}
Imobiliária: ${leadData.imobiliaria || "Não informado"}
Setor/função: ${role || "Não informado"}
WhatsApp: ${leadData.whatsapp || "Não informado"}
E-mail: ${leadData.email || "Não informado"}
Cidade/Estado: ${leadData.cidade || "Não informado"}
Quantidade aproximada de imóveis: ${leadData.imoveis || "Não informado"}
Principal desafio: ${leadData.desafio || "Não informado"}

Resultado do diagnóstico: ${diagnosis.name}
Pontuação: ${score} de ${questions.length * 3}

Resumo das respostas:
${resumo}

Comentário adicional: ${leadData.comentario || "Não informado"}`;

    return `https://wa.me/${FLEXPRO_WHATSAPP}?text=${encodeURIComponent(msg)}`;
  }

  return (
    <div className={styles.page}>
      {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && (
        <Script
          id="recaptcha-enterprise-diagnostico"
          src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
          strategy="afterInteractive"
        />
      )}
      <header className={styles.header}>
        <Link href="/" aria-label="Flexpro">
          <Image src="/images/logo-flex.png" alt="Flexpro Sistemas" width={140} height={36} priority />
        </Link>
        <span>Diagnóstico rápido para imobiliárias</span>
      </header>

      <main className={styles.quizShell}>
        <section className={styles.introPanel}>
          <div className={styles.eyebrow}>
            <Clock3 size={16} />
            Leva poucos minutos
          </div>
          <h1>
            Descubra se a gestão da sua imobiliária está <span>pronta para crescer</span>.
          </h1>
          <p>
            Responda um diagnóstico simples sobre rotina, leads, contratos, financeiro e processos. No final, você entende o cenário da sua operação e pode conversar com a Flexpro de forma consultiva.
          </p>
          <div className={styles.proof}>
            <span>Sem venda forçada</span>
            <span>Foco em gestão imobiliária</span>
            <span>Resultado consultivo</span>
          </div>
        </section>

        <section className={styles.quizCard}>
            {stage === "role" && (
              <>
                <div className={styles.progressWrap}>
                  <div className={styles.progress} style={{ width: "0%" }} />
                </div>
                <p className={styles.kicker}>Etapa inicial</p>
                <h1 className={styles.question}>Para começar, qual é o seu papel na imobiliária?</h1>
                <p className={styles.lead}>
                  Essa resposta ajuda a personalizar a conversa e entender melhor os desafios da sua rotina.
                </p>
                <div className={styles.options}>
                  {roles.map(({ label, detail, icon: Icon }) => (
                    <button className={styles.option} key={label} type="button" onClick={() => selectRole(label)}>
                      <span className={styles.optionIcon}>
                        <Icon size={18} strokeWidth={2} />
                      </span>
                      <span>
                        <strong>{label}</strong>
                        <small>{detail}</small>
                      </span>
                    </button>
                  ))}
                </div>
                <p className={styles.note}>
                  Depois disso, você responde algumas perguntas e vê depoimentos reais de imobiliárias que estruturaram melhor a gestão.
                </p>
              </>
            )}

            {stage === "quiz" && (
              <>
                <div className={styles.progressWrap}>
                  <div className={styles.progress} style={{ width: `${progress}%` }} />
                </div>
                <p className={styles.kicker}>Pergunta {step + 1} de {questions.length}</p>
                <p className={styles.roleLine}>Setor informado: <strong>{role}</strong></p>
                <h1 className={styles.question}>{currentQuestion.title}</h1>
                <div className={styles.options}>
                  {currentQuestion.options.map(([label], index) => (
                    <button
                      className={`${styles.option} ${selectedAnswer?.index === index ? styles.selected : ""}`}
                      key={label}
                      type="button"
                      onClick={() => selectOption(index)}
                    >
                      <span className={styles.dot} />
                      <span>
                        <strong>{label}</strong>
                        <small>{optionHelp[index]}</small>
                      </span>
                    </button>
                  ))}
                </div>
                <div className={styles.actions}>
                  <button className={styles.secondaryButton} type="button" onClick={goBack}>
                    <ArrowLeft size={16} />
                    Voltar
                  </button>
                  <button className={styles.primaryButton} type="button" onClick={nextQuestion} disabled={!selectedAnswer}>
                    Continuar
                    <ArrowRight size={16} />
                  </button>
                </div>
              </>
            )}

            {stage === "lead" && (
              <form className={styles.form} onSubmit={submitLead}>
                <input
                  type="text"
                  name="_honeypot"
                  style={{ display: "none", visibility: "hidden", position: "absolute", left: "-9999px", top: "-9999px" }}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={honeypot}
                  onChange={(event) => setHoneypot(event.target.value)}
                />
                <p className={styles.kicker}>Processando diagnóstico</p>
                <h1 className={styles.question}>As informações da sua imobiliária estão sendo processadas.</h1>
                <p className={styles.lead}>
                  Coloque seus dados para receber o resultado do diagnóstico. Ele também será enviado pelo nosso comercial para que a conversa seja mais direcionada à realidade da sua imobiliária.
                </p>
                <div className={styles.callout}>
                  <strong>Por que pedimos isso antes do resultado?</strong>
                  <span>
                    Porque o diagnóstico depende do contexto da operação: porte da carteira, cidade, setor de atuação e principal desafio.
                  </span>
                </div>

                <div className={styles.formGrid}>
                  <label>
                    Nome
                    <input required name="nome" placeholder="Seu nome" />
                  </label>
                  <label>
                    Imobiliária
                    <input required name="imobiliaria" placeholder="Nome da imobiliária" />
                  </label>
                  <label>
                    WhatsApp
                    <input
                      required
                      name="whatsapp"
                      type="tel"
                      inputMode="tel"
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                      onChange={(event) => {
                        event.currentTarget.value = formatPhone(event.currentTarget.value);
                      }}
                    />
                  </label>
                  <label>
                    E-mail
                    <input
                      required
                      name="email"
                      type="email"
                      inputMode="email"
                      placeholder="seu@email.com"
                      onChange={(event) => {
                        event.currentTarget.value = formatEmail(event.currentTarget.value);
                      }}
                    />
                  </label>
                  <label>
                    Cidade/Estado
                    <input required name="cidade" placeholder="Ex: Chapecó/SC" />
                  </label>
                  <label>
                    Quantidade aproximada de imóveis
                    <select required name="imoveis" defaultValue="">
                      <option value="" disabled>Selecione</option>
                      <option>Até 100 imóveis</option>
                      <option>101 a 300 imóveis</option>
                      <option>301 a 700 imóveis</option>
                      <option>701 a 1.500 imóveis</option>
                      <option>Mais de 1.500 imóveis</option>
                    </select>
                  </label>
                  <label>
                    Principal desafio hoje
                    <select required name="desafio" defaultValue="">
                      <option value="" disabled>Selecione</option>
                      <option>Organização da operação</option>
                      <option>Controle financeiro</option>
                      <option>Captação e atendimento de leads</option>
                      <option>Retrabalho e planilhas</option>
                      <option>Site, CRM e integrações</option>
                      <option>Gestão por dados</option>
                    </select>
                  </label>
                </div>
                <label>
                  Quer comentar algo sobre sua realidade?
                  <textarea name="comentario" placeholder="Ex: usamos planilhas, queremos melhorar o CRM, precisamos organizar locação..." />
                </label>
                {submitStatus === "error" && (
                  <p className={styles.errorMessage}>{errorMessage}</p>
                )}
                <div className={styles.actions}>
                  <button className={styles.secondaryButton} type="button" onClick={goBack}>
                    <ArrowLeft size={16} />
                    Voltar
                  </button>
                  <button className={styles.primaryButton} type="submit" disabled={submitStatus === "loading"}>
                    {submitStatus === "loading" ? "Enviando..." : "Receber meu resultado"}
                    <ArrowRight size={16} />
                  </button>
                </div>
              </form>
            )}

            {stage === "loading" && (
              <div className={styles.center}>
                <div className={styles.loader} />
                <p className={styles.kicker}>Analisando suas respostas</p>
                <h1 className={styles.question}>Estamos montando o diagnóstico da sua operação.</h1>
                <p className={styles.lead}>
                  Agora você verá seu resultado e alguns depoimentos reais de imobiliárias que organizaram melhor a gestão com a Flexpro.
                </p>
              </div>
            )}

            {stage === "result" && (
              <>
                <div className={styles.resultBadge}>Seu cenário: {diagnosis.name}</div>
                <h1 className={styles.question}>Antes de falar de sistema, veja o que muda quando o caos operacional começa a ser eliminado.</h1>
                <p className={styles.lead}>
                  Selecionamos depoimentos reais de clientes que estruturaram processos, reduziram retrabalho e ganharam mais controle na rotina.
                </p>
                <div className={styles.testimonialGrid}>
                  {testimonials.map((item) => (
                    <article className={styles.testimonial} key={item.name}>
                      <div className={styles.stars}>★★★★★</div>
                      <p>“{item.quote}”</p>
                      <strong>{item.name}</strong>
                      <span>{item.role}</span>
                    </article>
                  ))}
                </div>
                <div className={styles.summary}>
                  <p><strong>O que o seu diagnóstico indicou:</strong></p>
                  <p>{diagnosis.text}</p>
                  <p><strong>Ponto de atenção:</strong> {diagnosis.tip}</p>
                  <small>Pontuação: {score} de {questions.length * 3}</small>
                </div>
                <div className={styles.actionsRight}>
                  <button className={styles.primaryButton} type="button" onClick={() => setStage("about")}>
                    Continuar
                    <ArrowRight size={16} />
                  </button>
                </div>
              </>
            )}

            {stage === "about" && (
              <>
                <p className={styles.kicker}>Quem é a Flexpro Sistemas</p>
                <h1 className={styles.question}>Tecnologia para eliminar o caos da operação imobiliária.</h1>
                <p className={styles.lead}>
                  A Flexpro atende imobiliárias com um ecossistema de soluções para gestão imobiliária, unindo ERP, CRM, site integrado, aplicativos e ferramentas que ajudam a centralizar a operação e reduzir retrabalho.
                </p>
                <div className={styles.aboutImage}>
                  <Image
                    src="/images/features/simob.png"
                    alt="Gestão central da operação imobiliária"
                    width={900}
                    height={520}
                  />
                </div>
                <div className={styles.aboutBox}>
                  <h2>Na prática, a Flexpro ajuda a imobiliária a:</h2>
                  <div>
                    <span>Centralizar informações de imóveis, pessoas, contratos, atendimentos e financeiro.</span>
                    <span>Reduzir retrabalho em rotinas que antes dependiam de planilhas e controles manuais.</span>
                    <span>Acompanhar leads, oportunidades e produtividade da equipe comercial.</span>
                    <span>Tomar decisões com base em dados mais claros sobre a operação.</span>
                    <span>Criar uma rotina mais integrada entre gestão, atendimento, site, CRM e aplicativos.</span>
                  </div>
                </div>
                <div className={styles.callout}>
                  A proposta não é vender uma solução igual para todo mundo. A conversa consultiva serve para entender o momento da sua imobiliária e avaliar onde existem oportunidades reais de organização.
                </div>
                <div className={styles.actions}>
                  <button className={styles.secondaryButton} type="button" onClick={goBack}>
                    <ArrowLeft size={16} />
                    Voltar
                  </button>
                  <button className={styles.primaryButton} type="button" onClick={() => setStage("whatsapp")}>
                    Chamar no WhatsApp
                    <MessageCircle size={16} />
                  </button>
                </div>
              </>
            )}

            {stage === "whatsapp" && (
              <div className={styles.center}>
                <div className={styles.resultBadge}>Diagnóstico pronto</div>
                <h1 className={styles.question}>Agora é só chamar a Flexpro.</h1>
                <p className={styles.lead}>
                  Preparamos uma mensagem com seus dados e o resultado do diagnóstico para facilitar a conversa consultiva.
                </p>
                <Link className={styles.whatsappButton} href={buildWhatsappUrl()} target="_blank" rel="noopener noreferrer">
                  <MessageCircle size={18} />
                  Abrir conversa no WhatsApp
                </Link>
                <p className={styles.scoreLine}>Resultado: <strong>{diagnosis.name}</strong> | Pontuação: <strong>{score} de {questions.length * 3}</strong></p>
                <button className={styles.secondaryButton} type="button" onClick={() => setStage("about")}>
                  <ArrowLeft size={16} />
                  Voltar
                </button>
              </div>
            )}
        </section>
      </main>

      <section className={styles.stepsSection}>
        <div className={styles.stepsGrid}>
          <article>
            <Check size={20} />
            <strong>1. Diagnóstico</strong>
            <p>Algumas perguntas objetivas para entender o nível de organização da sua operação.</p>
          </article>
          <article>
            <Users size={20} />
            <strong>2. Histórias reais</strong>
            <p>Depois do quiz, você vê depoimentos de clientes que saíram do excesso de controles manuais.</p>
          </article>
          <article>
            <PieChart size={20} />
            <strong>3. Conversa consultiva</strong>
            <p>No final, você deixa seu contato para falar com um especialista sobre sua realidade.</p>
          </article>
        </div>
      </section>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div>
            <Image src="/images/logo-branca.png" alt="Flexpro" width={140} height={36} />
            <p>Tecnologia imobiliária que organiza a operação e sustenta o crescimento com controle.</p>
          </div>
          <nav>
            <Link href="/sobre-a-flexpro">Sobre nós</Link>
            <Link href="/contato">Contato</Link>
            <Link href="/politica-privacidade">Política de Privacidade</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
